import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  Input,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { MdOutlineFeedback } from "react-icons/md";
import { AiFillStar } from "react-icons/ai";
import axios from "axios";

const API = process.env.REACT_APP_PROPERTY_LINK_API_URL || "https://api.linkbase.ca";
const apiHeaders = {
  "Content-Type": "application/json",
  "x-api-key": process.env.REACT_APP_LINKBASE_API_KEY,
};

const fieldBox = {
  bg: "whiteAlpha.50",
  border: "1px solid",
  borderColor: "whiteAlpha.200",
  borderRadius: "14px",
  p: 3,
};

const FieldLabel = ({ children }) => (
  <Text fontSize="11px" color="whiteAlpha.600" letterSpacing="0.08em" textTransform="uppercase" fontWeight={700} mb={1}>
    {children}
  </Text>
);

/** Guest → host feedback for manual-account property links. */
const FeedbackModal = ({ isOpen, onClose, roomId }) => {
  const [message, setMessage] = useState("");
  const [rating, setRating] = useState(0);
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setMessage("");
      setRating(0);
      setErrorMessage("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!message.trim()) {
      setErrorMessage("Please write a short message.");
      return;
    }
    setIsLoading(true);
    setErrorMessage("");
    try {
      await axios.post(
        `${API}/rooms/${encodeURIComponent(roomId)}/guest-feedback`,
        { message: message.trim(), rating: rating || undefined, guestName, guestContact },
        { headers: apiHeaders }
      );
      toast({ title: "Thanks for your feedback!", status: "success", duration: 3000, isClosable: true });
      onClose();
    } catch (error) {
      console.error("Error sending feedback:", error);
      setErrorMessage(error.response?.data?.error || "Couldn't send your feedback. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
      <DrawerOverlay />
      <DrawerContent
        maxH="90vh"
        borderTopRadius="2xl"
        bg="gray.900"
        color="white"
        maxW={{ base: "100%", md: "560px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex mx={3} my={4} alignItems="center" justifyContent="space-between">
          <Flex align="center" gap={3}>
            <Flex
              align="center"
              justify="center"
              boxSize="36px"
              borderRadius="full"
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
            >
              <Icon as={MdOutlineFeedback} boxSize={5} color="#817EFF" />
            </Flex>
            <Text fontSize="18px" fontWeight={800}>
              Send feedback
            </Text>
          </Flex>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>

        <DrawerBody px={4} pt={0} pb={6}>
          <Text fontSize="13px" color="whiteAlpha.700" mb={4}>
            Tell your host how your stay is going. Only they will see it.
          </Text>

          <Flex gap={1} mb={4}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Icon
                key={n}
                as={AiFillStar}
                boxSize={7}
                cursor="pointer"
                color={n <= rating ? "#817EFF" : "whiteAlpha.300"}
                onClick={() => setRating(n === rating ? 0 : n)}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
              />
            ))}
          </Flex>

          <Stack spacing={3}>
            <Box {...fieldBox} borderColor={errorMessage ? "red.400" : "whiteAlpha.200"}>
              <FieldLabel>Your feedback</FieldLabel>
              <Textarea
                variant="unstyled"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (errorMessage) setErrorMessage("");
                }}
                placeholder="What went well, or what could be better?"
                color="white"
                fontSize="15px"
                rows={4}
                maxLength={2000}
                resize="none"
                autoFocus
                _placeholder={{ color: "whiteAlpha.500" }}
              />
            </Box>
            <Flex gap={3}>
              <Box {...fieldBox} flex={1}>
                <FieldLabel>Name (optional)</FieldLabel>
                <Input
                  variant="unstyled"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  color="white"
                  fontSize="15px"
                  maxLength={120}
                />
              </Box>
              <Box {...fieldBox} flex={1}>
                <FieldLabel>Email or phone (optional)</FieldLabel>
                <Input
                  variant="unstyled"
                  value={guestContact}
                  onChange={(e) => setGuestContact(e.target.value)}
                  color="white"
                  fontSize="15px"
                  maxLength={200}
                />
              </Box>
            </Flex>
          </Stack>

          {errorMessage && (
            <Alert status="error" borderRadius="12px" mt={3} bg="red.900" color="red.100">
              <AlertIcon color="red.300" />
              <Text fontSize="13px">{errorMessage}</Text>
            </Alert>
          )}

          <Button
            mt={5}
            w="full"
            bg="#817EFF"
            color="white"
            h="48px"
            borderRadius="14px"
            fontSize="14px"
            fontWeight={700}
            _hover={{ bg: "#615edc" }}
            _active={{ bg: "#4b49c4" }}
            onClick={handleSend}
            isLoading={isLoading}
            loadingText="Sending..."
          >
            Send feedback
          </Button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default FeedbackModal;
